/**
 * /forgot-password — request a reset email.
 *
 * Backend always returns 200 regardless of whether the email exists
 * (no enumeration). We mirror that: success card just confirms
 * "if an account exists with that email…".
 */

import { MarkEmailReadOutlined } from '@mui/icons-material';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { forgotPasswordRequest } from '../../api/auth';
import { AuthLayout } from './AuthLayout';
import { AuthPrimaryButton } from './AuthPrimaryButton';
import { AuthTextField } from './AuthTextField';
import { describeError } from './errorMessages';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await forgotPasswordRequest({ email });
      setSubmitted(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout
        title="Check your email"
        footer={
          <>
            <Link to="/login">Back to sign-in</Link>
          </>
        }
      >
        <Stack spacing={2.5} alignItems="center" sx={{ py: 1 }}>
          <MarkEmailReadOutlined sx={{ fontSize: 56, color: 'primary.main' }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1">
              If an account exists for <strong>{email}</strong>, we've sent it a password-reset
              link.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              The link is valid for 24 hours.
            </Typography>
          </Box>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot password"
      subtitle="Enter your account email and we'll send you a reset link."
      footer={
        <>
          Remembered it? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <AuthTextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus
          />

          <AuthPrimaryButton type="submit" disabled={!email} loading={submitting}>
            Send reset link
          </AuthPrimaryButton>
        </Stack>
      </form>
    </AuthLayout>
  );
}
